import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import AddGmailFilterStepTwo from './add-gmail-filter-step-two';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  betaAddGmailFilter,
  checkMatchingEmails,
  UserKeys,
} from '../../../../../api/users-api';
import { ModuleList } from '../../../../../shared/analytics/module-list';
import { handleErrors } from '../../../../../utils/errors';
import { useStore } from '../../../../state/zustand-store';
import { useAnalytics } from '../../../../../shared/analytics/create-events';
import * as analytics from '../../../../../shared/analytics/analytics.json';
import AddGmailFilterStepOne from './add-gmail-filter-step-one';
import { FilterModel, LabelModel } from '../../../../../../types';
import ChangeLogDialog from '../../../../../shared/change-log-dialog';
import {
  isAbleToSeeUIElement,
  UserRoles,
} from '../../../../../utils/roles/client-roles';
import { Messages } from '../../../../../utils/message-constants';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Close from '@mui/icons-material/Close';
interface AddGmailFilterDialogProps {
  open: boolean;
  onClose: () => void;
  userEmail: string;
  userRoles: string[];
  changeLogRoles: string[];
  sortedGmailLabels: LabelModel[];
}

export interface FilterForm {
  from: string;
  to: string;
  subject: string;
  query: string;
  negatedQuery: string;
  hasAttachment: boolean;
  excludeChats: boolean;
  size: number;
  sizeComparison: string;
  sizeType: string;
  forward: string;
  hasForward: boolean;
  archive: boolean;
  markRead: boolean;
  star: boolean;
  hasLabel: boolean;
  applyLabel: string;
  delete: boolean;
  neverSpam: boolean;
  markImportant: boolean;
  neverMarkImportant: boolean;
  category: string;
  hasCategory: boolean;
  applyToExisting: boolean;
}

const CriteriaSchema = z
  .object({
    from: z.string(),
    to: z.string(),
    subject: z.string(),
    query: z.string(),
    negatedQuery: z.string(),
    hasAttachment: z.boolean(),
    excludeChats: z.boolean(),
    size: z.number().min(0),
    sizeComparison: z.string(),
    sizeType: z.string(),
    step1Criteria: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    const hasStep1Criteria =
      data.from.trim().length > 0 ||
      data.to.trim().length > 0 ||
      data.subject.trim().length > 0 ||
      data.query.trim().length > 0 ||
      data.negatedQuery.trim().length > 0 ||
      data.size > 0 ||
      data.hasAttachment;

    if (!hasStep1Criteria) {
      ctx.addIssue({
        code: 'custom',
        message:
          'Please select at least one option from this section for your filter criteria.',
        path: ['step1Criteria'],
      });
    }
  });

const ActionSchema = z
  .object({
    forward: z.string(),
    hasForward: z.boolean(),
    archive: z.boolean(),
    markRead: z.boolean(),
    star: z.boolean(),
    hasLabel: z.boolean(),
    applyLabel: z.string(),
    delete: z.boolean(),
    neverSpam: z.boolean(),
    markImportant: z.boolean(),
    neverMarkImportant: z.boolean(),
    category: z.string(),
    hasCategory: z.boolean(),
    applyToExisting: z.boolean(),
    step2Action: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    const hasStep2Action =
      (data.hasForward && data.forward.trim().length > 0) ||
      data.archive ||
      data.markRead ||
      data.star ||
      (data.hasLabel && data.applyLabel.trim().length > 0) ||
      data.delete ||
      data.neverSpam ||
      data.markImportant ||
      data.neverMarkImportant ||
      (data.hasCategory && data.category.trim().length > 0);

    if (data.hasLabel && !data.applyLabel) {
      ctx.addIssue({
        code: 'custom',
        message: 'Please select a label.',
        path: ['applyLabel'],
      });
    }

    if (data.hasCategory && !data.category) {
      ctx.addIssue({
        code: 'custom',
        message: 'Please select a category.',
        path: ['category'],
      });
    }

    if (data.hasForward && !data.forward) {
      ctx.addIssue({
        code: 'custom',
        message: 'Please enter a forward email address.',
        path: ['forward'],
      });
    }

    if (!hasStep2Action) {
      ctx.addIssue({
        code: 'custom',
        message:
          'Please select at least one option from this section for your filter action.',
        path: ['step2Action'],
      });
    }
  });

const filterFormSchema = CriteriaSchema.and(ActionSchema);

export default function AddGmailFilterDialog({
  open,
  onClose,
  userEmail,
  userRoles,
  changeLogRoles,
  sortedGmailLabels,
}: AddGmailFilterDialogProps) {
  const steps = ['Search Criteria', 'Gmail Action'];
  const [activeStep, setActiveStep] = useState(0);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { setSnackbar } = useStore();
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const queryClient = useQueryClient();

  const {
    handleSubmit,
    watch,
    control,
    setValue,
    getValues,
    reset,
    trigger,
    setError,
    clearErrors,
    formState: { isValidating, isSubmitting, errors },
  } = useForm<FilterForm>({
    resolver: zodResolver(filterFormSchema),
    values: {
      from: '',
      to: '',
      subject: '',
      query: '',
      negatedQuery: '',
      hasAttachment: false,
      excludeChats: false,
      size: 0,
      sizeComparison: 'larger',
      sizeType: 'MB',
      forward: '',
      hasForward: false,
      archive: false,
      markRead: false,
      star: false,
      hasLabel: false,
      applyLabel: '',
      delete: false,
      neverSpam: false,
      markImportant: false,
      neverMarkImportant: false,
      category: '',
      hasCategory: false,
      applyToExisting: false,
    },
  });

  const currentFilterFormValues = watch();

  function getCriteriaQueryString(criteriaModel: FilterForm): string {
    const s: string[] = [];
    if (criteriaModel.from) {
      s.push(`from:(${criteriaModel.from})`);
    }
    if (criteriaModel.to) {
      s.push(`to:(${criteriaModel.to})`);
    }
    if (criteriaModel.subject) {
      s.push(`subject:(${criteriaModel.subject})`);
    }
    if (criteriaModel.query?.trim()) {
      s.push(`(${criteriaModel.query})`);
    }
    if (criteriaModel.negatedQuery) {
      s.push(`-(${criteriaModel.negatedQuery})`);
    }
    if (criteriaModel.hasAttachment) {
      s.push('has:attachment');
    }
    if (criteriaModel.excludeChats) {
      s.push('-is:chat');
    }
    if (
      criteriaModel.size > 0 &&
      criteriaModel.sizeComparison &&
      criteriaModel.sizeType
    ) {
      const comparisonOperator = criteriaModel.sizeComparison.toLowerCase();
      const sizeUnit = criteriaModel.sizeType.toUpperCase();
      if (
        (comparisonOperator === 'smaller' || comparisonOperator === 'larger') &&
        (sizeUnit === 'B' || sizeUnit === 'K' || sizeUnit === 'M')
      ) {
        s.push(`${comparisonOperator}:${criteriaModel.size}${sizeUnit}`);
      }
    }

    return s.join(' ').trim();
  }

  const { data: userMails, isFetching: isFetchingUserMails } = useQuery({
    queryKey: [
      UserKeys.UserMails,
      userEmail,
      {
        from: currentFilterFormValues.from,
        to: currentFilterFormValues.to,
        subject: currentFilterFormValues.subject,
        query: currentFilterFormValues.query,
        negatedQuery: currentFilterFormValues.negatedQuery,
        hasAttachment: currentFilterFormValues.hasAttachment,
        excludeChats: currentFilterFormValues.excludeChats,
        size: currentFilterFormValues.size,
        sizeComparison: currentFilterFormValues.sizeComparison,
        sizeType: currentFilterFormValues.sizeType,
      },
    ] as const,
    queryFn: ({ queryKey }) => {
      const [_key, _email, filterForm] = queryKey;
      const query = getCriteriaQueryString({
        ...currentFilterFormValues,
        ...filterForm,
      });
      return checkMatchingEmails(userEmail, query);
    },
    enabled:
      open &&
      activeStep === 0 &&
      (currentFilterFormValues.from !== '' ||
        currentFilterFormValues.to !== '' ||
        currentFilterFormValues.subject !== '' ||
        currentFilterFormValues.query !== '' ||
        currentFilterFormValues.negatedQuery !== '' ||
        currentFilterFormValues.hasAttachment ||
        currentFilterFormValues.excludeChats ||
        currentFilterFormValues.size !== 0),
  });

  const onSubmit: SubmitHandler<FilterForm> = (data) => {
    if (isAbleToSeeUIElement(changeLogRoles, UserRoles.AddGmailFilters.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(data);
    }
  };

  const { mutate, isPending: isAdding } = useMutation({
    mutationFn: (data: FilterForm) => {
      const criteria = {
        from: data.from,
        to: data.to,
        subject: data.subject,
        query: data.query,
        negatedQuery: data.negatedQuery,
        hasAttachment: data.hasAttachment,
        excludeChats: data.excludeChats,
        size:
          data.sizeType === 'MB'
            ? data.size * 1000000
            : data.sizeType === 'KB'
            ? data.size * 1000
            : data.size,
        sizeComparison: data.sizeComparison,
        sizeType: data.sizeType,
      };

      const addLabelIds: string[] = [];
      const removeLabelIds: string[] = [];

      if (data.archive) removeLabelIds.push('INBOX');
      if (data.markRead) removeLabelIds.push('UNREAD');
      if (data.star) addLabelIds.push('STARRED');
      if (data.delete) addLabelIds.push('TRASH');
      if (data.neverSpam) removeLabelIds.push('SPAM');
      if (data.markImportant) addLabelIds.push('IMPORTANT');
      if (data.neverMarkImportant) removeLabelIds.push('IMPORTANT');
      if (data.hasCategory && data.category) addLabelIds.push(data.category);
      if (data.hasLabel && data.applyLabel) addLabelIds.push(data.applyLabel);

      const action = {
        forward: data.hasForward ? data.forward : '',
        addLabelIds,
        removeLabelIds,
      };

      return betaAddGmailFilter(
        userEmail,
        {
          action,
          criteria,
        },
        changeLogMessage,
        data.applyToExisting
      );
    },
    onSuccess: (returnedData) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Created('Filter'),
      });
      setChangeLogMessage('');
      reset();
      onClose();

      queryClient.setQueryData(
        [UserKeys.GmailFilters, userEmail],
        (oldData: FilterModel[]) => {
          if (oldData) {
            return [...oldData, returnedData];
          } else {
            return [returnedData];
          }
        }
      );
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Creating('the filter'),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.gmail_settings
          .create_gmail_filter,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.filters,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  return (
    <Dialog fullWidth open={open} onClose={onClose}>
      <DialogTitle
        sx={{
          position: 'relative',
          alignContent: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        }}
        whiteSpace={'nowrap'}
        textOverflow={'ellipsis'}
        overflow={'hidden'}
      >
        {steps[activeStep]}
        <Box
          sx={{
            width: '96px',
            height: '2px',
            backgroundColor: 'primary.main',
            mx: 'auto',
          }}
        ></Box>
      </DialogTitle>
      <IconButton
        sx={{ position: 'absolute', right: 0, top: 0, m: 1 }}
        onClick={() => {
          onClose();
        }}
      >
        <Close />
      </IconButton>
      <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ p: 0 }}>
          <Box>
            {steps[activeStep] === 'Search Criteria' && (
              <AddGmailFilterStepOne
                control={control}
                setValue={setValue}
                errors={errors}
              />
            )}
            {steps[activeStep] === 'Gmail Action' && (
              <AddGmailFilterStepTwo
                control={control}
                clearErrors={clearErrors}
                getValues={getValues}
                setValue={setValue}
                watch={watch}
                userEmail={userEmail}
                userRoles={userRoles}
                changeLogRoles={changeLogRoles}
                sortedGmailLabels={sortedGmailLabels}
                errors={errors}
                userMails={userMails || []}
                isFetchingUserMails={isFetchingUserMails}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{ display: 'flex', justifyContent: 'space-between', p: 2 }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              setActiveStep((prevState) => prevState - 1);
            }}
            disabled={activeStep === 0}
          >
            Back
          </Button>
          <Button
            variant="contained"
            loading={isValidating || isSubmitting || isAdding}
            type={activeStep === steps.length - 1 ? 'submit' : 'button'}
            onClick={
              activeStep === 0
                ? async (e) => {
                    e.preventDefault(); // Only prevent on step 1

                    const step1Fields: (keyof FilterForm)[] = [
                      'from',
                      'to',
                      'subject',
                      'query',
                      'negatedQuery',
                      'hasAttachment',
                      'excludeChats',
                      'size',
                      'sizeComparison',
                      'sizeType',
                    ];
                    const isFieldsValid = await trigger(step1Fields);

                    const data = getValues();
                    const criteriaResult = CriteriaSchema.safeParse(data);

                    if (isFieldsValid && criteriaResult.success) {
                      clearErrors('step1Criteria' as keyof FilterForm);
                      setActiveStep((prevState) => prevState + 1);
                    } else if (!criteriaResult.success) {
                      setError('step1Criteria' as keyof FilterForm, {
                        type: 'custom',
                        message:
                          'Please select at least one option from this section for your filter criteria.',
                      });
                    }
                  }
                : undefined
            } // No onClick handler on final step
          >
            {activeStep === steps.length - 1 ? 'Create Filter' : 'Next'}
          </Button>
        </DialogActions>
      </Box>
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          mutate(getValues());
        }}
        mutateLoading={isAdding}
      />
    </Dialog>
  );
}
